import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  DeleteForever as DeleteForeverIcon,
} from '@mui/icons-material';
import { apiUrl, getStoredAdmin } from '../config/api';
import { toBranchDisplayName } from '../config/branchNames';

const ADMIN_API = apiUrl('admin_management');
const BRANCHES_API = apiUrl('branches');

const EMPTY_FORM = {
  id: 0,
  name: '',
  phone: '',
  role: 'branch_admin',
  branch_id: '',
  is_active: true,
};

const ROLE_OPTIONS = [
  { label: 'Branch Admin', value: 'branch_admin' },
  { label: 'Super Admin', value: 'super_admin' },
];

function normalizePhone(value) {
  return String(value || '').replace(/\D/g, '');
}

function fmtDateTime(value) {
  if (!value) return '—';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return String(value);
  return dt.toLocaleString();
}

function roleLabel(role) {
  return String(role || '') === 'super_admin' ? 'Super Admin' : 'Branch Admin';
}

function withBranchLabels(rows) {
  return (Array.isArray(rows) ? rows : []).map((r) => ({
    ...r,
    id: Number(r.id || 0),
    branch_id: r.branch_id == null ? null : Number(r.branch_id),
    is_active: Number(r.is_active || 0) === 1 || r.is_active === true,
    branch_name: toBranchDisplayName({
      branchId: r.branch_id,
      branchName: r.branch_name || '',
      branchCode: r.branch_code || '',
    }),
  }));
}

export default function AdminManagement() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const me = useMemo(() => getStoredAdmin(), []);
  const isSuperAdmin = String(me?.role || '') === 'super_admin';

  const [admins, setAdmins] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);

  const [form, setForm] = useState(EMPTY_FORM);

  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(EMPTY_FORM);

  const fallbackBranches = useMemo(() => withBranchLabels(me?.allowed_branches), [me?.allowed_branches]);

  const loadData = useCallback(async () => {
    if (!isSuperAdmin) return;
    setLoading(true);
    setError('');
    setNotice('');
    try {
      const [adminsRes, branchesRes] = await Promise.all([
        fetch(ADMIN_API),
        fetch(BRANCHES_API),
      ]);

      const adminsJson = await adminsRes.json().catch(() => ({}));
      if (!adminsRes.ok || String(adminsJson?.status || '').toLowerCase() === 'error') {
        throw new Error(adminsJson?.message || 'Failed to load admins');
      }
      setAdmins(withBranchLabels(adminsJson?.items));

      const branchesJson = await branchesRes.json().catch(() => ({}));
      if (!branchesRes.ok || String(branchesJson?.status || '').toLowerCase() === 'error') {
        setBranches(fallbackBranches);
      } else {
        const list = withBranchLabels(branchesJson?.items);
        setBranches(list.length ? list : fallbackBranches);
      }
    } catch (err) {
      setError(err?.message || 'Failed to load admin management data');
      setBranches(fallbackBranches);
      setAdmins([]);
    } finally {
      setLoading(false);
    }
  }, [fallbackBranches, isSuperAdmin]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const activeBranchOptions = useMemo(
    () => (Array.isArray(branches) ? branches.filter((b) => b && b.id > 0) : []),
    [branches]
  );
  const selfAdminId = Number(me?.id || 0);
  const isSelfAdmin = useCallback((item) => Number(item?.id || 0) === selfAdminId, [selfAdminId]);
  const selectableIds = useMemo(
    () => admins.filter((a) => !isSelfAdmin(a)).map((a) => Number(a.id)),
    [admins, isSelfAdmin]
  );
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const selectedCount = selectedIds.length;
  const allSelected = selectableIds.length > 0 && selectedCount === selectableIds.length;
  const someSelected = selectedCount > 0 && selectedCount < selectableIds.length;

  useEffect(() => {
    const valid = new Set(admins.map((a) => Number(a.id)));
    setSelectedIds((prev) => prev.filter((id) => valid.has(id) && id !== selfAdminId));
  }, [admins, selfAdminId]);

  const setCreateField = (name, value) => {
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'role' && value === 'super_admin') {
        next.branch_id = '';
      }
      return next;
    });
  };

  const setEditField = (name, value) => {
    setEditForm((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'role' && value === 'super_admin') {
        next.branch_id = '';
      }
      return next;
    });
  };

  const toggleSelect = (id, checked) => {
    const normalized = Number(id || 0);
    if (!normalized || normalized === selfAdminId) return;
    setSelectedIds((prev) => {
      if (checked) {
        if (prev.includes(normalized)) return prev;
        return [...prev, normalized];
      }
      return prev.filter((itemId) => itemId !== normalized);
    });
  };

  const toggleSelectAll = (checked) => {
    setSelectedIds(checked ? selectableIds : []);
  };

  const runBulkAction = async ({ action, doneMessage, confirmMessage, requireDeletePrompt = false }) => {
    if (!selectedIds.length) return;
    if (confirmMessage && !window.confirm(confirmMessage)) return;
    if (requireDeletePrompt) {
      const typed = window.prompt(
        `Type DELETE to permanently delete ${selectedIds.length} admin account(s). This cannot be undone.`
      );
      if (typed !== 'DELETE') return;
    }

    setError('');
    setNotice('');
    setSaving(true);
    try {
      const res = await fetch(ADMIN_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, ids: selectedIds }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || String(json?.status || '').toLowerCase() === 'error') {
        throw new Error(json?.message || 'Bulk action failed');
      }
      const affected = Number(json?.affected_rows || 0);
      setNotice(`${doneMessage} (${affected} affected)`);
      setSelectedIds([]);
      await loadData();
    } catch (err) {
      setError(err?.message || 'Bulk action failed');
    } finally {
      setSaving(false);
    }
  };

  const buildPayload = (current, action) => {
    const role = String(current.role || '').trim();
    const phone = normalizePhone(current.phone);
    const name = String(current.name || '').trim();
    const branchId = role === 'branch_admin' ? Number(current.branch_id || 0) : null;
    const isActive = current.is_active ? 1 : 0;

    if (!name) throw new Error('Name is required');
    if (!/^\d{7,15}$/.test(phone)) throw new Error('Phone must be 7 to 15 digits');
    if (role !== 'super_admin' && role !== 'branch_admin') throw new Error('Invalid role');
    if (role === 'branch_admin' && !branchId) throw new Error('Branch is required for branch admin');

    const payload = {
      action,
      name,
      phone,
      role,
      branch_id: branchId,
      is_active: isActive,
    };
    if (action === 'update') {
      payload.id = Number(current.id || 0);
      if (!payload.id) throw new Error('Invalid admin id');
    }
    return payload;
  };

  const createAdmin = async () => {
    setError('');
    setNotice('');
    let payload;
    try {
      payload = buildPayload(form, 'add');
    } catch (err) {
      setError(err.message);
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(ADMIN_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || String(json?.status || '').toLowerCase() === 'error') {
        throw new Error(json?.message || 'Failed to create admin');
      }

      setForm(EMPTY_FORM);
      setNotice('Admin account created');
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to create admin');
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (item) => {
    setEditForm({
      id: Number(item.id || 0),
      name: String(item.name || ''),
      phone: normalizePhone(item.phone),
      role: String(item.role || 'branch_admin'),
      branch_id: item.branch_id == null ? '' : Number(item.branch_id),
      is_active: Number(item.is_active || 0) === 1 || item.is_active === true,
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    setError('');
    setNotice('');
    let payload;
    try {
      payload = buildPayload(editForm, 'update');
    } catch (err) {
      setError(err.message);
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(ADMIN_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || String(json?.status || '').toLowerCase() === 'error') {
        throw new Error(json?.message || 'Failed to update admin');
      }
      setEditOpen(false);
      setNotice('Admin account updated');
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to update admin');
    } finally {
      setSaving(false);
    }
  };

  const deactivateAdmin = async (item) => {
    if (!window.confirm(`Deactivate admin ${item.name}?`)) return;
    setError('');
    setNotice('');
    setSaving(true);
    try {
      const res = await fetch(ADMIN_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'delete', id: Number(item.id || 0) }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || String(json?.status || '').toLowerCase() === 'error') {
        throw new Error(json?.message || 'Failed to deactivate admin');
      }
      setNotice('Admin account deactivated');
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to deactivate admin');
    } finally {
      setSaving(false);
    }
  };

  const hardDeleteAdmin = async (item) => {
    const typed = window.prompt(
      `Type DELETE to permanently remove admin "${item.name}". This cannot be undone.`
    );
    if (typed !== 'DELETE') return;

    setError('');
    setNotice('');
    setSaving(true);
    try {
      const res = await fetch(ADMIN_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'hard_delete', id: Number(item.id || 0) }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || String(json?.status || '').toLowerCase() === 'error') {
        throw new Error(json?.message || 'Failed to permanently delete admin');
      }
      setNotice('Admin account permanently deleted');
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to permanently delete admin');
    } finally {
      setSaving(false);
    }
  };

  const activateAdmin = async (item) => {
    setError('');
    setNotice('');
    let payload;
    try {
      payload = buildPayload(
        {
          ...item,
          id: Number(item.id || 0),
          name: String(item.name || ''),
          phone: normalizePhone(item.phone),
          role: String(item.role || 'branch_admin'),
          branch_id: item.branch_id == null ? '' : Number(item.branch_id),
          is_active: true,
        },
        'update'
      );
    } catch (err) {
      setError(err.message);
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(ADMIN_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || String(json?.status || '').toLowerCase() === 'error') {
        throw new Error(json?.message || 'Failed to activate admin');
      }
      setNotice('Admin account activated');
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to activate admin');
    } finally {
      setSaving(false);
    }
  };

  if (!isSuperAdmin) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning">Only super admins can manage admin accounts.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h4">Admin Management</Typography>
        <Button startIcon={<RefreshIcon />} variant="outlined" onClick={loadData} disabled={loading || saving}>
          Refresh
        </Button>
      </Stack>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      {notice ? <Alert severity="success" sx={{ mb: 2 }}>{notice}</Alert> : null}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
        >
          <Typography variant="body1">
            Selected admins: <b>{selectedCount}</b>
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button
              variant="outlined"
              onClick={() => toggleSelectAll(true)}
              disabled={saving || !selectableIds.length || allSelected}
            >
              Select All
            </Button>
            <Button
              variant="outlined"
              onClick={() => toggleSelectAll(false)}
              disabled={saving || !selectedCount}
            >
              Clear Selection
            </Button>
            <Button
              color="success"
              variant="outlined"
              onClick={() => runBulkAction({
                action: 'bulk_activate',
                doneMessage: 'Bulk activate completed',
                confirmMessage: `Activate ${selectedCount} selected admin(s)?`,
              })}
              disabled={saving || !selectedCount}
            >
              Activate Selected
            </Button>
            <Button
              color="warning"
              variant="outlined"
              onClick={() => runBulkAction({
                action: 'bulk_deactivate',
                doneMessage: 'Bulk deactivate completed',
                confirmMessage: `Deactivate ${selectedCount} selected admin(s)?`,
              })}
              disabled={saving || !selectedCount}
            >
              Deactivate Selected
            </Button>
            <Button
              color="error"
              variant="outlined"
              startIcon={<DeleteForeverIcon />}
              onClick={() => runBulkAction({
                action: 'bulk_hard_delete',
                doneMessage: 'Bulk permanent delete completed',
                requireDeletePrompt: true,
              })}
              disabled={saving || !selectedCount}
            >
              Delete Selected
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 1.5 }}>Create New Admin</Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            label="Name"
            value={form.name}
            onChange={(e) => setCreateField('name', e.target.value)}
            fullWidth
          />
          <TextField
            label="Phone"
            value={form.phone}
            onChange={(e) => setCreateField('phone', e.target.value)}
            fullWidth
          />
          <TextField
            select
            label="Role"
            value={form.role}
            onChange={(e) => setCreateField('role', e.target.value)}
            fullWidth
          >
            {ROLE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Branch"
            value={form.branch_id}
            onChange={(e) => setCreateField('branch_id', e.target.value)}
            fullWidth
            disabled={form.role !== 'branch_admin'}
          >
            {activeBranchOptions.map((b) => (
              <MenuItem key={b.id} value={b.id}>
                {b.name}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Switch
              checked={!!form.is_active}
              onChange={(e) => setCreateField('is_active', e.target.checked)}
            />
            <Typography variant="body2">Active account</Typography>
          </Stack>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            onClick={createAdmin}
            disabled={saving || loading}
          >
            Create Admin
          </Button>
        </Stack>
      </Paper>

      {loading ? (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <CircularProgress />
        </Box>
      ) : isMobile ? (
        <Stack spacing={2}>
          {admins.map((item) => (
            <Card key={item.id} variant="outlined">
              <CardContent>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="h6">{item.name}</Typography>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Checkbox
                      size="small"
                      checked={selectedSet.has(Number(item.id || 0))}
                      onChange={(e) => toggleSelect(item.id, e.target.checked)}
                      disabled={saving || isSelfAdmin(item)}
                    />
                    <Chip
                      label={item.is_active ? 'Active' : 'Inactive'}
                      color={item.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </Stack>
                </Stack>
                <Typography variant="body2"><b>Phone:</b> {item.phone}</Typography>
                <Typography variant="body2"><b>Role:</b> {roleLabel(item.role)}</Typography>
                <Typography variant="body2">
                  <b>Branch:</b> {item.role === 'super_admin' ? 'All Branches' : (item.branch_name || '—')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Updated: {fmtDateTime(item.updated_at)}
                </Typography>
              </CardContent>
              <CardActions sx={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                <IconButton color="primary" onClick={() => openEdit(item)}>
                  <EditIcon />
                </IconButton>
                {item.is_active ? (
                  <Button color="warning" onClick={() => deactivateAdmin(item)} disabled={saving}>
                    Deactivate
                  </Button>
                ) : (
                  <Button color="success" onClick={() => activateAdmin(item)} disabled={saving}>
                    Activate
                  </Button>
                )}
                <Button
                  color="error"
                  startIcon={<DeleteForeverIcon />}
                  onClick={() => hardDeleteAdmin(item)}
                  disabled={saving || Number(item.id || 0) === Number(me?.id || 0)}
                >
                  Delete
                </Button>
              </CardActions>
            </Card>
          ))}
          {!admins.length ? <Typography align="center">No admin accounts found.</Typography> : null}
        </Stack>
      ) : (
        <Paper>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={(e) => toggleSelectAll(e.target.checked)}
                    disabled={saving || !selectableIds.length}
                  />
                </TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Branch</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Updated</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {admins.map((item) => (
                <TableRow key={item.id}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedSet.has(Number(item.id || 0))}
                      onChange={(e) => toggleSelect(item.id, e.target.checked)}
                      disabled={saving || isSelfAdmin(item)}
                    />
                  </TableCell>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>{item.phone}</TableCell>
                  <TableCell>{roleLabel(item.role)}</TableCell>
                  <TableCell>{item.role === 'super_admin' ? 'All Branches' : (item.branch_name || '—')}</TableCell>
                  <TableCell>
                    <Chip
                      label={item.is_active ? 'Active' : 'Inactive'}
                      color={item.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{fmtDateTime(item.updated_at)}</TableCell>
                  <TableCell align="center">
                    <IconButton color="primary" onClick={() => openEdit(item)}>
                      <EditIcon />
                    </IconButton>
                    {item.is_active ? (
                      <Button color="warning" onClick={() => deactivateAdmin(item)} disabled={saving}>
                        Deactivate
                      </Button>
                    ) : (
                      <Button color="success" onClick={() => activateAdmin(item)} disabled={saving}>
                        Activate
                      </Button>
                    )}
                    <Button
                      color="error"
                      startIcon={<DeleteForeverIcon />}
                      onClick={() => hardDeleteAdmin(item)}
                      disabled={saving || Number(item.id || 0) === Number(me?.id || 0)}
                    >
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!admins.length ? (
                <TableRow>
                  <TableCell align="center" colSpan={8}>No admin accounts found.</TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Admin</DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 2, pt: 2 }}>
          <TextField
            label="Name"
            value={editForm.name}
            onChange={(e) => setEditField('name', e.target.value)}
            fullWidth
          />
          <TextField
            label="Phone"
            value={editForm.phone}
            onChange={(e) => setEditField('phone', e.target.value)}
            fullWidth
          />
          <TextField
            select
            label="Role"
            value={editForm.role}
            onChange={(e) => setEditField('role', e.target.value)}
            fullWidth
          >
            {ROLE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Branch"
            value={editForm.branch_id}
            onChange={(e) => setEditField('branch_id', e.target.value)}
            fullWidth
            disabled={editForm.role !== 'branch_admin'}
          >
            {activeBranchOptions.map((b) => (
              <MenuItem key={b.id} value={b.id}>
                {b.name}
              </MenuItem>
            ))}
          </TextField>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Switch
              checked={!!editForm.is_active}
              onChange={(e) => setEditField('is_active', e.target.checked)}
            />
            <Typography variant="body2">Active account</Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveEdit} disabled={saving}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
